// src/pages/ManageSocialMedia.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoGridOutline,
    IoCheckboxOutline,
    IoChatboxEllipsesOutline,
    IoPodiumOutline,
    IoBarChartOutline
} from 'react-icons/io5';
import image1 from '../assets/images/ConnectSocial.png';
import image2 from '../assets/images/Calender.png';
import image3 from '../assets/images/Messages.png';
import image4 from '../assets/images/Analytics.png';
import heroBg from '../assets/images/manage-social-media-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{
    "@type": "ListItem",
    "position": 1,
    "name": "Home",
    "item": "https://oolook.in/"
  },{
    "@type": "ListItem",
    "position": 2,
    "name": "Manage Social Media",
    "item": "https://oolook.in/manage-social-media"
  }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Social Media Management Software",
    "provider": {
        "@type": "Organization",
        "name": "Oolook"
    },
    "name": "All-in-One Social Media Management Platform",
    "description": "Streamline your entire social media workflow with Oolook. Manage all your accounts, schedule content, engage with your audience, and analyze performance from a single, intelligent platform.",
    "url": "https://oolook.in/manage-social-media"
};

// --- Page-Specific Data ---
const manageSocialMediaPageData = {
  seo: {
    title: "All-in-One Social Media Management Tool | Oolook",
    description: "Simplify your social media workflow. Manage all your accounts, schedule posts, engage with your audience, and track performance from one central dashboard. Powered by AI.",
    image: 'https://oolook.in/og/manage-social-media.png', // Ensure this OG image exists
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoGridOutline,
    title: "One Platform to Rule Them All",
    subtitle: "Stop the chaos of switching between tabs and tools. Oolook unifies your entire social media presence into a single, smart command center, giving you complete control.",
    backgroundImage: heroBg
  },
  featureSection: {
    title: "A Seamless Workflow, From Start to Finish",
    subtitle: "Oolook is intelligently designed to connect every part of your social media strategy, eliminating friction and saving you time.",
    features: [
      { title: "Connect All Your Accounts", description: "Securely link Instagram, Facebook, X, LinkedIn, and more. Manage everything without ever leaving the Oolook dashboard.", image: image1 },
      { title: "Plan & Schedule Visually", description: "Use our intuitive content calendar to plan, draft, approve, and schedule all your posts across every platform.", image: image2 },
      { title: "Engage in One Inbox", description: "Bring all your DMs, comments, and mentions into a single, unified feed. Never miss an important conversation again.", image: image3 },
      { title: "Analyze Total Performance", description: "See the big picture with cross-platform analytics that show you what's working and how your overall strategy is performing.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Advantage of Unified Management",
    benefits: [
      { icon: IoCheckboxOutline, title: "Boost Efficiency", description: "Dramatically reduce the time spent on administrative tasks and focus on creating high-impact content." },
      { icon: IoChatboxEllipsesOutline, title: "Strengthen Your Brand", description: "Maintain a consistent voice, posting schedule, and visual identity across all your social channels." },
      { icon: IoPodiumOutline, title: "Improve Performance", description: "Make smarter, data-driven decisions when you can see how all your channels are working together." },
      { icon: IoBarChartOutline, title: "Scale with Confidence", description: "Easily manage more clients, team members, and social profiles as your business grows." }
    ]
  },
  stepByStep: {
    title: "Get Full Control in Minutes",
    subtitle: "Onboarding is fast and intuitive, designed to get you managing your accounts right away.",
    steps: [
      { title: "1. Connect Your Profiles", description: "Our guided setup helps you securely connect all your social media accounts in just a few clicks." },
      { title: "2. Set Up Your Calendar", description: "Create your first post, upload your media to the asset library, and drag it onto the calendar to schedule." },
      { title: "3. Configure Your Inbox", description: "Your messages and comments will start flowing into the Unified Inbox automatically. No complex setup required." },
      { title: "4. Explore Your Dashboard", description: "Once connected, your analytics dashboard will begin populating with real-time data from all your accounts." }
    ]
  },
  targetAudience: {
    title: "The Command Center for Modern Teams"
  }
};

const ManageSocialMedia = () => {
  return (
    <FeaturePageLayout pageData={manageSocialMediaPageData} />
  );
};

export default ManageSocialMedia;